/**
 * Stored status of an Eingang (offen | erledigt, 0141), not the task-derived "Bearbeitung".
 * live → GET/PATCH /posteingang/:commId/status; mock → in-memory store. A change refetches
 * the list, the counts and this status.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PosteingangState } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { isLiveData } from '../../../lib/data-mode';
import { qk } from '../../../lib/query-keys';
import { mockEingangStatus, setMockEingangStatus } from '../mock';

/** The stored status of one Eingang, for the Bearbeitungsmaske header. */
export function useEingangStatus(commId: string | null) {
  const live = isLiveData();
  return useQuery<PosteingangState>({
    queryKey: qk.posteingangStatus(commId ?? 'none', live ? 'live' : 'mock'),
    queryFn: async () =>
      live
        ? (await apiClient.posteingang.getStatus(commId as string)).status
        : mockEingangStatus(commId as string),
    enabled: Boolean(commId),
    ...(live ? {} : { initialData: () => (commId ? mockEingangStatus(commId) : undefined) }),
  });
}

/** One click: set offen | erledigt. Rejects on failure so the dropdown can revert. */
export function useSetEingangStatus() {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: async ({ commId, status }: { commId: string; status: PosteingangState }) => {
      if (live) return apiClient.posteingang.setStatus(commId, status);
      setMockEingangStatus(commId, status);
      return { id: commId, status, changed_at: new Date().toISOString() };
    },
    onSuccess: (_data, { commId }) => {
      void qc.invalidateQueries({ queryKey: qk.posteingang() });
      void qc.invalidateQueries({ queryKey: ['posteingang', 'counts'] });
      void qc.invalidateQueries({ queryKey: ['posteingang', 'status', commId] });
    },
  });
}
